import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import MobileSticky from "../components/MobileSticky";
import QuoteForm from "../components/QuoteForm";
import Testimonials from "../components/Testimonials";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import Seo, { localBusinessSchema, breadcrumbSchema } from "../components/Seo";
import { COMPANY, SERVICES, SUBURBS, TESTIMONIALS, GALLERY } from "../data/site";

const HERO = "https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?crop=entropy&cs=srgb&fm=jpg&w=2200&q=85";

export default function Home() {
  return (
    <div data-testid="page-home">
      <Seo
        title="Sunshine Coast Painters | Connolly's Painting Solutions"
        description="Connolly's Painting Solutions — professional painters on the Sunshine Coast. Interior, exterior, roof and body corporate painting from Noosa to Caloundra. Fixed-price quotes."
        path="/"
        schema={[
          localBusinessSchema(COMPANY, { reviews: TESTIMONIALS }),
          breadcrumbSchema([
            { name: "Home", url: `${typeof window !== "undefined" ? window.location.origin : ""}/` },
          ]),
        ]}
      />
      <Header />

      {/* HERO — full-screen on mobile with vertical breathing room */}
      <section className="relative bg-[#0B1426] text-white overflow-hidden min-h-screen md:min-h-0 flex flex-col" data-testid="hero">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-55" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1426]/40 via-[#0B1426]/50 to-[#0B1426]" />
        </div>
        <div className="relative container-cps pt-20 md:pt-32 pb-20 md:pb-24 flex-1 flex flex-col justify-center">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 w-full">
            <div className="lg:col-span-8">
              <div className="overline mb-6">Sunshine Coast · Est. 2012</div>
              <h1 className="font-display text-[2.4rem] leading-[1.1] md:text-6xl lg:text-[4.75rem] md:leading-[1.02] tracking-tight">
                Professional Painters{" "}<br/>on the <span style={{ color: "#C5A059" }}>Sunshine Coast.</span>
              </h1>
              <p className="mt-7 md:mt-8 text-base md:text-xl text-white/75 max-w-2xl leading-[1.7] md:leading-relaxed">
                Painting, finished with intent. Premium interior, exterior, roof and body corporate painting for Sunshine Coast homes and businesses — careful prep, considered finishes, tidy crews.
              </p>
              <div className="mt-8 md:mt-10 flex flex-col md:flex-row gap-3">
                <Link to="/quote" className="btn-gold text-center" data-testid="hero-cta-quote">Get a Free Quote</Link>
                <div className="hidden md:block">
                  <Link to="/gallery" className="btn-ghost-light text-center" data-testid="hero-cta-work">View Our Work</Link>
                </div>
              </div>

              {/* Stats — flow naturally after the CTAs, with comfortable spacing */}
              <div className="mt-12 md:mt-16 hairline-gold" />
              <div className="mt-6 md:mt-8 grid grid-cols-3 md:grid-cols-4 gap-6 md:gap-8 text-sm max-w-3xl">
                {[
                  { k: "20+", l: "Years on Coast" },
                  { k: "640+", l: "Homes painted" },
                  { k: "4.9★", l: "Average review" },
                  { k: "100%", l: "Licensed & Insured", hideMobile: true },
                ].map((s) => (
                  <div key={s.l} className={s.hideMobile ? "hidden md:block" : ""}>
                    <div className="font-display text-2xl md:text-4xl text-white">{s.k}</div>
                    <div className="text-white/55 mt-1.5 tracking-wide text-[11px] md:text-sm">{s.l}</div>
                  </div>
                ))}
              </div>

              {/* Mobile-only Licensed & Insured marker (matches footer treatment) */}
              <div className="mt-8 md:hidden" data-testid="hero-licensed-mobile">
                <div className="hairline-gold w-10" />
                <div className="overline mt-3" style={{ color: "rgba(255,255,255,0.65)" }}>
                  Licensed &amp; Insured
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES — horizontal snap-scroll on mobile, grid on desktop */}
      <section className="section bg-[#F9F8F6]" data-testid="services-section">
        <div className="container-cps">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 items-end mb-8 md:mb-16">
            <div className="md:col-span-7">
              <div className="overline">What we do</div>
              <h2 className="font-display text-3xl md:text-5xl mt-3 md:mt-4 tracking-tight leading-tight">
                Considered finishes,<br/>across every surface.
              </h2>
            </div>
            <p className="md:col-span-5 text-[#4F5B6A] leading-relaxed text-[15px]">
              From whole-home interiors to coastal-tough exteriors, roof restoration, body corporate and commercial repaints, and decorative finishes. Each job runs through the same process.
            </p>
          </div>

          {/* Mobile: stacked horizontal cards — uniform */}
          <div className="lg:hidden grid grid-cols-1 gap-px bg-[#E5E3DB]" data-testid="services-mobile-grid">
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/${s.slug}`} className="bg-[#F9F8F6] flex items-stretch" data-testid={`home-service-${s.slug}`}>
                <div className="w-[42%] aspect-square overflow-hidden bg-[#EEEAE0] flex-shrink-0">
                  <img src={s.card || s.hero} alt={s.name} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 px-5 py-5 flex flex-col justify-center">
                  <h3 className="font-display text-lg text-[#0B1426] leading-tight">{s.name}</h3>
                  <p className="text-[13px] text-[#4F5B6A] mt-2 leading-relaxed line-clamp-3">{s.short}</p>
                  <div className="mt-3 text-[10px] uppercase tracking-[0.18em] text-[#C5A059]">Explore →</div>
                </div>
              </Link>
            ))}
          </div>

          {/* Desktop: full grid */}
          <div className="hidden lg:grid grid-cols-5 gap-px bg-[#E5E3DB]">
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/${s.slug}`} className="group bg-[#F9F8F6] flex flex-col" data-testid={`home-service-desk-${s.slug}`}>
                <div className="aspect-[4/5] overflow-hidden bg-[#EEEAE0]">
                  <img src={s.card || s.hero} alt={s.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                </div>
                <div className="px-6 pt-6 pb-7">
                  <h3 className="font-display text-[1.4rem] leading-tight text-[#0B1426] group-hover:text-[#C5A059] transition-colors">{s.name}</h3>
                  <p className="text-sm text-[#4F5B6A] mt-2 leading-relaxed">{s.short}</p>
                </div>
              </Link>
            ))}
          </div>

          {/* Featured — Body Corporate Painters (SEO callout) */}
          <div className="mt-8 md:mt-10">
            <Link
              to="/body-corporate-painters-sunshine-coast"
              className="group flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-6 bg-[#0B1426] text-white px-6 md:px-10 py-6 md:py-8 hover:bg-[#131f3a] transition-colors"
              data-testid="home-body-corporate-callout"
            >
              <div>
                <div className="overline" style={{ color: "rgba(197,160,89,0.9)" }}>Featured</div>
                <h3 className="font-display text-xl md:text-2xl mt-2 tracking-tight">
                  Body Corporate Painters — Sunshine Coast
                </h3>
                <p className="text-white/70 text-[14px] md:text-[15px] mt-2 leading-relaxed max-w-2xl">
                  Apartment complexes, strata common areas, exterior repaints — coordinated around residents with fixed-price quoting.
                </p>
              </div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#C5A059] border-b border-[#C5A059] pb-1 whitespace-nowrap">
                Learn more <ArrowRight size={14} />
              </div>
            </Link>
          </div>
        </div>
      </section>
      <section className="section bg-white" data-testid="recent-work-section">
        <div className="container-cps">
          {/* Mobile: bold gallery teaser */}
          <div className="lg:hidden">
            <Link to="/gallery" className="block relative overflow-hidden group" data-testid="home-gallery-teaser">
              <div className="aspect-[4/5] bg-[#0B1426] relative">
                <img src={GALLERY[0].after} alt="Recent work" loading="lazy" className="w-full h-full object-cover opacity-80" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1426] via-[#0B1426]/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-7 text-white">
                  <div className="overline" style={{ color: "rgba(255,255,255,0.7)" }}>The work</div>
                  <h2 className="font-display text-[2rem] leading-[1.1] mt-3 tracking-tight">
                    See the<br/>transformations.
                  </h2>
                  <p className="mt-4 text-white/75 text-[15px] leading-relaxed">
                    Before-and-after galleries from homes across the Sunshine Coast.
                  </p>
                  <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#C5A059] border-b border-[#C5A059] pb-1">
                    View Full Gallery <ArrowRight size={14} />
                  </div>
                </div>
              </div>
            </Link>
          </div>

          {/* Desktop: 4 before/after sliders */}
          <div className="hidden lg:block">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
              <div>
                <div className="overline">Recent projects</div>
                <h2 className="font-display text-5xl mt-4 tracking-tight">Selected work.</h2>
              </div>
              <Link to="/gallery" className="text-link text-sm uppercase tracking-[0.18em] text-[#0B1426]" data-testid="home-view-gallery">
                View full gallery →
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-8">
              {[GALLERY[0], GALLERY[1], GALLERY[2], GALLERY[6]].map((g, i) => (
                <article key={g.id} className="bg-white" data-testid={`home-project-${i}`}>
                  <BeforeAfterSlider before={g.before} after={g.after} height="aspect-[4/3]" testId={`home-ba-${i}`} />
                  <div className="mt-4 flex items-start justify-between gap-4">
                    <p className="font-display text-xl text-[#0B1426] leading-tight">{g.title}</p>
                    <span className="text-xs uppercase tracking-wider text-[#4F5B6A] whitespace-nowrap pt-1.5">{g.service}</span>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY US — 2 cards on mobile, 4 on desktop */}
      <section className="section bg-[#0B1426] text-white" data-testid="why-section">
        <div className="container-cps grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          <div className="md:col-span-5">
            <div className="overline">Why Connolly's</div>
            <h2 className="font-display text-3xl md:text-5xl mt-3 md:mt-4 tracking-tight leading-tight">
              Considered, careful, finished properly.
            </h2>
            <p className="mt-4 md:mt-6 text-white/70 leading-relaxed text-[15px] md:text-base">
              A small, owner-led crew that turns up when we say, treats your home like ours, and finishes the job the way we'd want it done.
            </p>
            <div className="hairline-gold mt-6 md:mt-8" />
            <p className="mt-4 md:mt-6 text-xs md:text-sm uppercase tracking-[0.18em] text-white/55">
              Sunshine Coast · 20 years on the Coast
            </p>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {[
              { t: "Preparation, properly.", d: "Filling, sanding, undercoating — the unsexy work that separates a finish that lasts from one that doesn't." },
              { t: "Premium products only.", d: "Dulux, Haymes, Resene. Specified to the substrate and your exposure. Never own-brand off-the-shelf." },
              { t: "One crew, start to finish.", d: "No sub-contracted unknowns. The same painters quote, prep, paint and sign off your job.", hideMobile: true },
              { t: "Tidy job sites.", d: "Drop sheets, masked edges, daily clean-down. You barely know we're there — until the finish reveals itself.", hideMobile: true },
            ].map((b) => (
              <div key={b.t} className={b.hideMobile ? "hidden md:block" : ""}>
                <div className="hairline-gold mb-3 md:mb-4" />
                <h3 className="font-display text-xl md:text-2xl text-white">{b.t}</h3>
                <p className="mt-2 md:mt-3 text-white/70 leading-relaxed text-[15px] md:text-base">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — 1 on mobile, 4 on desktop */}
      <section className="section bg-[#F9F8F6]" data-testid="home-testimonials">
        <div className="container-cps">
          <div className="text-center max-w-2xl mx-auto mb-10 md:mb-16">
            <div className="overline">Reviews</div>
            <h2 className="font-display text-3xl md:text-5xl mt-3 md:mt-4 tracking-tight">What our clients say.</h2>
            <div className="mt-5 flex flex-col items-center gap-1.5">
              <div className="flex gap-0.5" aria-label="4.9 of 5 stars">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={16} fill="#C5A059" stroke="#C5A059" />
                ))}
              </div>
              <div className="text-sm text-[#0B1426]">
                <span className="font-display text-lg">4.9 / 5</span>
                <span className="text-[#4F5B6A] ml-2 text-xs uppercase tracking-wider">137+ Verified reviews</span>
              </div>
            </div>
          </div>
          <div className="md:hidden">
            <Testimonials limit={1} />
          </div>
          <div className="hidden md:block">
            <Testimonials limit={4} />
          </div>
          <div className="mt-10 md:mt-12 text-center">
            <Link to="/reviews" className="btn-secondary" data-testid="home-all-reviews">Read All Reviews</Link>
          </div>
        </div>
      </section>

      {/* SUBURBS — 3 cols on mobile (was 2), shorter overall */}
      <section className="section bg-white" data-testid="home-suburbs">
        <div className="container-cps">
          <div className="text-center max-w-2xl mx-auto mb-10 md:mb-16">
            <div className="overline">Service areas</div>
            <h2 className="font-display text-3xl md:text-5xl mt-3 md:mt-4 tracking-tight">Painters across the Sunshine Coast.</h2>
            <p className="text-[#4F5B6A] mt-4 md:mt-5 text-[15px] md:text-base">
              From <Link to="/painters-noosa" className="text-[#0B1426] hover:text-[#C5A059] underline decoration-[#C5A059]/40 decoration-1 underline-offset-4">Noosa</Link> and <Link to="/painters-maroochydore" className="text-[#0B1426] hover:text-[#C5A059] underline decoration-[#C5A059]/40 decoration-1 underline-offset-4">Maroochydore</Link> through to <Link to="/painters-caloundra" className="text-[#0B1426] hover:text-[#C5A059] underline decoration-[#C5A059]/40 decoration-1 underline-offset-4">Caloundra</Link> — local crews who know the homes and the climate.
            </p>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-4 gap-px bg-[#E5E3DB]">
            {SUBURBS.map((s) => (
              <Link key={s.slug} to={`/${s.slug}`} className="bg-white p-4 md:p-8 hover:bg-[#F9F8F6] transition-colors min-h-[88px] md:min-h-0" data-testid={`home-suburb-${s.slug}`}>
                <div className="font-display text-[15px] md:text-xl text-[#0B1426] leading-[1.2]">{s.name}</div>
                <div className="text-[10px] md:text-xs uppercase tracking-wider text-[#4F5B6A] mt-1">QLD {s.postcode}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE CTA — banner on mobile (no form), full form on desktop */}
      <section className="section bg-[#0B1426] text-white" data-testid="home-quote-section">
        <div className="container-cps">
          {/* Mobile: compact CTA banner */}
          <div className="lg:hidden text-center max-w-2xl mx-auto">
            <div className="overline" style={{ color: "rgba(255,255,255,0.7)" }}>Free quote today</div>
            <h2 className="font-display text-3xl mt-3 tracking-tight leading-tight">Ready to start your project?</h2>
            <p className="text-white/70 mt-4 text-[15px] leading-relaxed">
              We respond within one business day with a clear, fixed-price quote.
            </p>
            <div className="mt-8 flex flex-col items-stretch gap-3">
              <Link to="/quote" className="btn-gold w-full" data-testid="home-mobile-quote">
                Get a Free Quote <ArrowRight size={14} className="ml-2" />
              </Link>
              <a href={`tel:${COMPANY.phoneTel}`} className="btn-ghost-light w-full" data-testid="home-mobile-call">
                Call {COMPANY.phone}
              </a>
            </div>
          </div>

          {/* Desktop: full inline form */}
          <div className="hidden lg:grid grid-cols-12 gap-12">
            <div className="col-span-5">
              <div className="overline">Free quote today</div>
              <h2 className="font-display text-5xl mt-4 tracking-tight">Tell us about your project.</h2>
              <p className="text-white/70 mt-5 leading-relaxed max-w-md">
                Most quotes returned within 48 hours. For complex jobs we'll arrange an on-site walk-through at no cost.
              </p>
              <div className="mt-10 space-y-3 text-white/75 text-sm">
                <div>📞 <a href={`tel:${COMPANY.phoneTel}`} className="hover:text-white" data-testid="home-quote-phone">{COMPANY.phone}</a></div>
                <div>✉ <a href={`mailto:${COMPANY.email}`} className="hover:text-white">{COMPANY.email}</a></div>
                <div>📍 Servicing the entire Sunshine Coast</div>
              </div>
            </div>
            <div className="col-span-7 bg-white text-[#0B1426] p-12">
              <QuoteForm sourcePage="home" />
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <MobileSticky />
    </div>
  );
}
